import { useRef, useState } from "react";

export default function ZoomableImage({ children }) {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [lastTap, setLastTap] = useState(0);

  const MIN = 1;
  const MAX = 3;

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const clampPosition = (nextScale, nextX, nextY) => {
    const container = containerRef.current;
    if (!container) return { x: 0, y: 0 };

    const rect = container.getBoundingClientRect();
    const overflowX = (rect.width * nextScale - rect.width) / 2;
    const overflowY = (rect.height * nextScale - rect.height) / 2;

    return {
      x: clamp(nextX, -overflowX, overflowX),
      y: clamp(nextY, -overflowY, overflowY),
    };
  };

  /** Desktop scroll to zoom */
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.2 : 0.2;

    const nextScale = clamp(scale + delta, MIN, MAX);
    const clamped = clampPosition(nextScale, pos.x, pos.y);

    setScale(nextScale);
    setPos(clamped);
  };

  /** Drag to pan */
  const handleMouseDown = (e) => {
    if (scale === 1) return;

    const startX = e.clientX - pos.x;
    const startY = e.clientY - pos.y;

    const onMove = (ev) => {
      const nextX = ev.clientX - startX;
      const nextY = ev.clientY - startY;

      const clamped = clampPosition(scale, nextX, nextY);
      setPos(clamped);
    };

    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  /** Double-tap to zoom (mobile) */
  const handleTouchStart = (e) => {
    const now = Date.now();
    if (now - lastTap < 250) {
      const nextScale = scale === 1 ? 2 : 1;
      setScale(nextScale);
      setPos({ x: 0, y: 0 });
    }
    setLastTap(now);
  };

  return (
    <div
      ref={containerRef}
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      style={{
        width: "100%",
        height: "100%",
        overflow: "hidden",
        borderRadius: 8,
        position: "relative",
        cursor: scale > 1 ? "grab" : "default",
      }}
    >
      <div
        style={{
          width: "100%",
          height: "100%",
          transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
          transformOrigin: "center center",
          transition: scale === 1 ? "transform .2s ease-out" : "none",
        }}
      >
        {children}
      </div>
    </div>
  );
}
